import type {
  ConfirmationStatusType,
  SatuanPendidikanType,
} from './kinerja-pelaksanaan'
import type { UseQueryOptions } from 'react-query'
import type { CommonListResponse, CommonResponse } from 'types/api'
import type { KinerjaStatusGpk } from 'types/kinerja-gpk'

export type KinerjaStatus = KinerjaStatusOriginal | KinerjaStatusGpk

export type KinerjaStatusOriginal =
  | 'NOT_FOUND' // Belum diajukan Guru
  | 'DRAFT' // Belum diajukan Guru
  | 'WAITING_APPROVAL' // Sudah diajukan Guru, menunggu persetujuan dari KS
  | 'APPROVED' // Sudah disetujui KS, menunggu sepakati Guru
  | 'REVISED' // Sedang direvisi KS, tapi belum disetujui
  | 'READY' // Sudah sepakati Guru, siap untuk pelaksanaan
  | 'WAITING_EVALUATION' // Deprecated

export type KinerjaRealisasiState =
  | 'PENDING_SUBMISSION'
  | 'WAITING_EVALUATION'
  | 'EVALUATED'
  | 'DRAFT'

export type Jenjang =
  | 'pertama'
  | 'muda'
  | 'madya'
  | 'utama'
  | 'ahli pertama'
  | 'ahli muda'
  | 'ahli madya'
  | 'ahli utama'
  | 'bawah pertama'

export type KategoriKinerja =
  | 'kinerja'
  | 'pembelajaran'
  | 'kompetensi'
  | 'tambahan'
  | 'minat'

export const listOfPangkat = [
  'LTIII/A',
  'III/A',
  'III/B',
  'III/C',
  'III/D',
  'IV/A',
  'IV/B',
  'IV/C',
  'IV/D',
  'IV/E',
] as const

export enum KinerjaPeriod {
  SEMESTER_1 = 1,
  SEMESTER_2 = 2,
}

export type Pangkat = typeof listOfPangkat[number]

export interface Kinerja {
  id: string
  tahun: number
  period: number
  status: KinerjaStatus | KinerjaStatusGpk
  pangkatGolongan: string
  jenjang: string
  nama?: string

  kinerjaUtama: number[]
  praktikPembelajaran: number
  pengembanganKompetensi: KinerjaSelection[]
  tugasTambahan: KinerjaSelection[]
  prilakuKerja: PerilakuKerjaPayload[]

  reviewKinerjaUtama?: number[]
  reviewPraktikPembelajaran: number
  reviewPengembanganKompetensi: KinerjaSelection[]
  reviewTugasTambahan: KinerjaSelection[]
  reviewPrilakuKerja: Changeable<PerilakuKerjaPayload>[]

  statusUnor: KonfirmasiUnorStatus
  statusPengaliran: TipePengaliranStatus

  revisi?: string
  tipe?: KinerjaTipe

  ekinEligible: boolean
  kategoriAsn: boolean
  isPadan: boolean
}

export type KinerjaWithRole<T> = T & {
  /**
   * @deprecated use `tipe` instead
   */
  role?: KinerjaRoleType
  tipe?: KinerjaTipe
}

export type KinerjaTipe = 'GPK' | 'SPF' | 'SPNF'

export type RequestParamsKinerja = KinerjaWithRole<{
  jenjang?: Jenjang | 'UNDEFINED'
  ptkId?: string
  periodParams?: MultiPeriodParams
}>

export type RequestParamsGetKinerja = KinerjaWithRole<{
  ptkId: string
}>

export type RequestParamsGetReviewSkpV2 = Partial<MultiPeriodPayload> & {
  ptkId: string
  tipe: KinerjaTipe
}

export type ResponseKinerja = CommonResponse<Kinerja>

export type KategoriPegawai = 'UNDEFINED' | 'PNS' | 'PPPK'
export type KinerjaSPNFRoleType =
  | 'GURU_SPNF'
  | 'KS_PLT_SPNF'
  | 'KS_DEFINITIF_SPNF'
export type KinerjaBaseRoleType = 'KS_PLT' | 'KS_DEFINITIF' | 'GURU'
export type KinerjaGpkRoleType = 'GURU_GPK'
export type KinerjaRoleType =
  | KinerjaBaseRoleType
  | KinerjaSPNFRoleType
  | KinerjaGpkRoleType

export type KinerjaTeachingRoleType = 'GURU' | 'GURU_SPNF'

export type KinerjaHeadmasterCommonRoleType = 'KS' | 'KSPNF'

export interface KinerjaProfilRole {
  npsn: string
  type: KinerjaRoleType
  satuanPendidikan: string
  statusSatuanPendidikan: SatuanPendidikanType
  confirmationStatus: ConfirmationStatusType
}

export interface KinerjaProfil {
  nama: string
  pangkatGolongan: string
  jenjang: string
  satuanPendidikan: string
  nip: string
  nuptk?: string
  ksAktif: boolean
  npsn: string
  statusPegawai: string
  kategoriPegawai: KategoriPegawai
  roles?: Array<KinerjaProfilRole>
  timKinerja?: string
  pejabatPenilaiKinerja?: string
  statusSatuanPendidikan?: SatuanPendidikanType
}

export type ResponseKinerjaProfil = CommonResponse<KinerjaProfil>

export type RequestKinerjaProfile = {
  params?: { userId?: string; ptkId?: string }
  options?: UseQueryOptions<ResponseKinerjaProfil>
  isDemoMode?: boolean
}

export interface KinerjaDetail {
  title: string
  resultFile: string
  result: string
  targetQty: number
  targetDeadline: string
  example: string
  qualityIndicators: string[]
  profile: boolean
  grade: string
  notes: string
}

export interface BuktiDukung {
  id: number
  deskripsi: string
}

export interface KinerjaUtamaDetail {
  id: number
  order?: number
  rencanaHasilKerja: string
  buktiDukung: BuktiDukung[]
  targetKuantitas: string
  targetWaktu: number
  targetKualitas: string
  targetKuantitasMin?: number
  targetKuantitasMax?: number
  nama?: string
  definisi?: string
  bobot: number
  catatan: string
  tag?: string
}

export interface KinerjaTambahanDetail {
  id?: number
  order?: number
  deskripsi?: string
  options: KinerjaUtamaDetail[]
}

export interface KinerjaPraktikPembelajaranContentIndikator {
  id: number
  nama: string
  definisi: string
  rencanaHasilKerja: string
  targetKualitas: string
  buktiDukung: BuktiDukung[]
  targetKuantitas: string
  targetWaktu: number

  fokusObservasi?: Array<string>
}

export interface KinerjaPraktikPembelajaranDimensi {
  dimensiId: string
  dimensiNama: string
  skor: number
  indikator: KinerjaPraktikPembelajaranContentIndikator[]
}

export interface KinerjaTugasTambahan {
  buktiDukung: BuktiDukung
  id: number
  rencanaHasilKerja: string
  targetKualitas: string
  targetKuantitas: string
  targetKuantitasMax: number
  targetKuantitasMin: number
  targetWaktu: number
}

export interface ContentKinerjaUtama {
  jenjangId: number
  jenjang: string
  kinerjaUtama: KinerjaUtamaDetail[]

  isRaporExists: boolean
  praktikPembelajaran: KinerjaPraktikPembelajaranDimensi[]
  pengembanganKompetensi: KinerjaUtamaDetail[]
  tugasTambahan: KinerjaUtamaDetail[]
}

export type ResponseContentKinerjaUtama = CommonResponse<ContentKinerjaUtama>

export interface PerilakuKerjaEkpektasi {
  id: number
  orderEkspektasi: number
  value: string
}

export interface PerilakuKerjaIndikator {
  id: number
  orderIndikator: number
  value: string
  ekspektasi: PerilakuKerjaEkpektasi[]
}

export interface PerilakuKerjaAspek {
  id: number
  orderAspek: number
  value: string
  indikator: PerilakuKerjaIndikator[]
}

export interface ContentPerilakuKerja {
  aspek: PerilakuKerjaAspek[]
}

export type ResponseContentPerilakuKerja = CommonResponse<ContentPerilakuKerja>

// "XOR" between two union types
// see: https://stackoverflow.com/questions/44425344/typescript-interface-with-xor-barstring-xor-cannumber
type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never }
type XOR<T, U> = T | U extends object
  ? (Without<T, U> & U) | (Without<U, T> & T)
  : T | U

export type Changeable<T> = T & { isChanged?: boolean }

export type CreateSKPPayload = KinerjaWithRole<{
  pangkatGolongan: string
  jenjang: string
  skpYear?: number
  skpPeriod?: number
}>

export interface KinerjaSelection {
  id: number
  kuantitas: number
}

export interface PerilakuKerjaPayload {
  aspekId: number
  indikatorId: number
  ekspektasiId: number
}

export type UpdateSKPPayload = KinerjaWithRole<{
  id: string
  // TODO: Remove these properties?
  kinerjaUtama?: number[]

  praktikPembelajaran?: number
  pengembanganKompetensi?: KinerjaSelection[]
  tugasTambahan?: KinerjaSelection[]
  prilakuKerja?: PerilakuKerjaPayload[]
  skpYear?: number
  skpPeriod?: number
}>

export type UpsertSKPPayload = XOR<CreateSKPPayload, UpdateSKPPayload>

export interface GuruSKP {
  userId: string
  ptkId: string
  name: string
  pangkatGolongan: string
  jenjang: string
  skpStatus: KinerjaStatus
  skpId: string
  kategoriPegawai: KategoriPegawai
}

export type ResponseGetListGuruByNpsn = CommonResponse<GuruSKP[]>

export interface UpsertReviewSKPPayload {
  ptkId: string
  reviewPraktikPembelajaran?: number
  reviewPengembanganKompetensi?: KinerjaSelection[]
  reviewTugasTambahan?: KinerjaSelection[] | [{}]
  reviewPrilakuKerja?: Changeable<PerilakuKerjaPayload>[]
  reviewKinerjaUtama?: number[]
  tipe?: KinerjaTipe
}

export type ResetSKPPayload = KinerjaWithRole<{
  ptkId?: string
  skpYear?: number
  skpPeriod?: number
}>

export type UpdateStatePayload = KinerjaWithRole<{
  skpId: string
}>

export type BulkUpdateStatePayload = {
  skpIds: Array<string>
  tipe: KinerjaTipe
}

export type BulkUpdateStateResponse = CommonResponse<{
  successCount: number
  failedCount: number
}>

export interface SKPTabHandler {
  saveDraft: () => void
}

export interface KinerjaPerencanaanTabBaseProps {
  skp: Kinerja
  isSubmitting?: boolean
  reviewMode?: boolean
  onClickNext?: (data: UpdateSKPPayload) => void
  onClickSave?: (data: UpdateSKPPayload) => void
}

export interface OverviewBuktiDukung {
  totalKinerja: number
  totalRealisasiKinerja: number
}

export interface OverviewRealisasi {
  period: KinerjaPeriod
  kinerjaUtama: OverviewBuktiDukung
  kinerjaTambahan: OverviewBuktiDukung
}

export interface Realisasi {
  id: string
  sequence: string
  kategoriKinerja: KategoriKinerja
  kategori: string
  deskripsi: string
  state: KinerjaRealisasiState
}

export interface RealisasiV2 {
  id: string
  deskripsi: string
  state: KinerjaRealisasiState
  bobot: number
  kategori: RealisasiTypeV2
  realisasi: number
}

interface KinerjaUpdatedDate {
  kategori: RealisasiTypeV2
  kinerjaUpdatedAt: string
}

export interface ResponseGetListRealisasiV2 {
  data: RealisasiV2[]
  bobotTotal: number
  kinerjaUpdated: KinerjaUpdatedDate[]
  realisasiTotal: number
  realisasiResult: RealisasiResultType
}

export type RealisasiResultType =
  | 'MEET_EXPECTATION'
  | 'EXCEED_EXPECTATION'
  | 'BELOW_EXPECTATION'

/**
 * Represents the type of realisasi.
 * Possible values are:
 * - 'KU': Kinerja Utama
 * - 'PP': Praktik Pembelajaran
 * - 'PK': Pengembangan Kompetensi
 * - 'TT': Tugas Tambahan
 */
export type RealisasiTypeV2 = 'KU' | 'PP' | 'PK' | 'TT'

export type ResponseGetOverviewRealisasi = CommonResponse<OverviewRealisasi[]>

export type ResponseGetListRealisasi = CommonResponse<Realisasi[]>

export interface BuktiDukungFileInstance {
  id: string
  deskripsi: string
  fileUrl: string
  fileName: string
}

export interface BuktiDukungRealisasi {
  sequence: string
  deskripsi: string
  targetKualitas: string
  state: KinerjaRealisasiState
  catatan: string
  bobot: number
  buktiDukung: BuktiDukungFileInstance[]
}

export interface RequestParamsRealisasi {
  realisasiId: string
  periodParams?: MultiPeriodParams
  userId?: string
}

export type ResponseBuktiDukung = CommonResponse<BuktiDukungRealisasi>

export enum SourceUploadDialogPage {
  HOME,
  BUKTI_KARYA_CATEGORIES,
  BUKTI_KARYA_DOCUMENT_LIST,
}

export interface SourceUploadDialogPageBaseProps<P> {
  onSetPage: (page: SourceUploadDialogPage, params?: P) => void
}

export interface BuktiKaryaCategory {
  id?: number
  name: string
  total: number
  key: string
  iconName: string
}

export interface TotalBuktiKaryaDocument {
  categoryId: string
  total: number
}

export type ResponseTotalBuktiKaryaDocument = CommonResponse<
  Array<TotalBuktiKaryaDocument>
>

export interface RequestUploadFromBuktiKarya {
  realisasiId: string
  state: string

  buktiDukung: Array<{
    id: string
    deskripsi: string
    fileUrl: string
    fileName: string
    buktiKaryaId: string
  }>
}

export interface SKPPeriod {
  period: number
  date: string
  periodeKinerja: PeriodeKinerja[]
}

export interface SKPPeriodV2 extends Omit<SKPPeriod, 'date'> {
  id: string
  startDate: string
  endDate: string
  isApproved: boolean
  accessible: boolean
}

export interface SKPPeriodV2Meta {
  latestActiveId: string
}

export type ResponseSKPPeriodV2 = CommonResponse<SKPPeriodV2[], SKPPeriodV2Meta>

export interface RequestParamsApproveSKPPeriod {
  periode_id: string
  is_approved: boolean
  npsn: string
}

export interface PeriodeKinerja {
  key: string
  value: string
}

export type KonfirmasiUnorStatus =
  | 'NOT_SUBMITTED'
  | 'UNOR_MATCH'
  | 'UNOR_UNMATCH'
  | 'UNOR_UNMATCH_SUPERIOR'
  | 'UNOR_UNMATCH_ORG'

export type TipePengaliranStatus = 'NOT_DELIVERED' | 'DELIVERED' | 'FAILED'

export interface UnorPegawai {
  nama: string
  unorIdPegawai: string
  satuanPendidikan: string
  namaAtasan: string
  unorIdAtasan: string
  nipAtasan: string
  statusAtasan: string
}

export interface MultiPeriodPayload {
  skpPeriod: number
  skpYear: number
}

export type CommonMultiPeriodMeta = { skpYear: number; skpPeriod: number }

export interface MultiPeriodParams {
  skp_period: number
  skp_year: number
}
export interface MultiYearParams {
  skp_year: number
}

export interface RequestGetReviewSKP {
  ptkId: string
  skp_year?: number
  skp_period?: number
}

export type ResponseGetTugasPokok = CommonResponse<{
  state: TugasPokokState
  updatedAt?: string
}>

export type TugasPokokState = 'SUBMITTED' | 'NOT_FOUND' | 'APPROVED'

export type ResponseGetPengembanganKompetensi = CommonResponse<{
  state: PengembanganKompetensiState
  competencyImprovement: {
    reflectionInspiration: string
    reflectionImplementation: string
  }
  updatedAt?: string
}>

export type PengembanganKompetensiState =
  | 'NOT_FOUND'
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'

export interface ParamsSubmitPengembanganKompetensi {
  state: Extract<PengembanganKompetensiState, 'DRAFT' | 'SUBMITTED'>
  reflectionInspiration: string
  reflectionImplementation: string
  tipe?: KinerjaTipe
}

export interface ParamsSubmitTugasPokok {
  tipe?: KinerjaTipe
}

export type ResponseGetSatuanPendidikanDocuments = CommonResponse<{
  flagTnc: boolean
}>

export interface ParamsApproveBuktiKinerja {
  tipe?: KinerjaTipe
  ptkId: string
  types: Array<BuktiKinerjaDocumentType>
}

export type BuktiKinerjaDocumentType = Extract<RealisasiTypeV2, 'PK' | 'TT'>

// Penilaian Periodik
export type PeriodikState = 'COMPLETED' | 'IN_PROGRESS' | 'NOT_STARTED'
export type PeriodikType = 'BULANAN' | 'TRIWULAN' | 'NOT_FOUND'
export type PeriodikPegawaiState =
  | 'NOT_SENT'
  | 'READY_TO_SEND'
  | 'SENT_TO_BKN'
  | 'SENT_TO_BKN_SUCCESS'
  | 'SENT_TO_BKN_FAILED'
  | 'SENT_TO_DINAS'
export type PeriodikPerformancePracticeResult =
  | 'BELOW_EXPECTATION'
  | 'MEET_EXPECTATION'
  | 'EXCEED_EXPECTATION'
export type PeriodikWorkBehaviorResult =
  | 'BELOW_EXPECTATION'
  | 'MEET_EXPECTATION'
  | 'EXCEED_EXPECTATION'
export type PeriodikPerformancePredicateResult =
  | 'VERY_GOOD'
  | 'GOOD'
  | 'NEED_IMPROVEMENT'
  | 'BAD'
  | 'VERY_BAD'
export type PeriodikRealization =
  | 'REALIZATION_EARLY'
  | 'REALIZATION_INTERMEDIATE'
  | 'REALIZATION_ADVANCED'

export type ParamsBasePeriodik = KinerjaWithRole<{ skpYear: number }>

export type ParamsGetPeriodikList = ParamsBasePeriodik & { npsn: string }
export type ResponseGetPeriodikList = CommonResponse<
  {
    periodicType: PeriodikType
    periods: Array<PeriodikItem>
  },
  { nilaiRekomendasi: boolean }
>
export type PeriodikItem = {
  month: number
  label: string
  state: PeriodikState
  startingDate: string
  endingDate: string
}

export type ParamsGetPeriodikType = ParamsBasePeriodik
export type ResponseGetPeriodikType = CommonResponse<{
  periodicType: PeriodikType
}>

export type ParamsGetPeriodikDetail = ParamsBasePeriodik & {
  npsn: string
  skpMonth: number
}
export type ResponseGetPeriodikDetail = CommonListResponse<
  PeriodikPegawaiItem,
  {
    periodicLabel: string
    endingDate: string
    nilaiRekomendasi: boolean
    periodikType: PeriodikType
  }
>

export type ParamsGetPeriodikPegawaiDetail = ParamsBasePeriodik & {
  ptkId: string
  skpMonth: number
}

export type ResponseGetPeriodikPegawaiDetail = CommonResponse<
  PeriodikPegawaiItem,
  { periodicLabel: string; nilaiRekomendasi: boolean }
>

export type PeriodikPegawaiItem = {
  feedback: string
  name: string
  perencanaanExist: boolean
  performancePractice: PeriodikPerformancePracticeResult
  predicatePeriodic: PeriodikPerformancePredicateResult
  ptkId: string
  realization: PeriodikRealization
  role: string
  schoolName?: string
  dinasName?: string
  state: PeriodikPegawaiState
  workBehavior: PeriodikWorkBehaviorResult
  reason?: string
  actionItems?: Array<string>
}

export type ParamsBulkPenilaianPeriodikPegawai = Pick<
  PeriodikPegawaiItem,
  'performancePractice' | 'workBehavior' | 'realization' | 'feedback'
> & {
  npsn: string
  ptkIds: Array<string>
  skpYear: number
  skpMonth: number
  tipe?: KinerjaTipe
}

export type ParamsBulkSendPeriodikToBkn = {
  npsn: string
  ptkIds: Array<string>
  skpMonth: number
  skpYear: number
}

export type ParamsResetPenilaianPeriodik = {
  npsn: string
  skpMonth: number
  skpYear: number
}

export type ParamsPerencanaanRevision = {
  ptkId: string
  revisi: string
  tipe: KinerjaTipe
}

export type EmployeeStatusType = 'PNS' | 'PPPK' | 'HONORER'

export type PerencanaanItemReviewDiff<T = string> = {
  original: T
  review: T
  isChanged: boolean
}

export type PerencanaanDetailData = {
  tugasTambahanIds: Array<number>
  tugasPokokIds: Array<number>
  praktikKinerjaId: number | null
  pengembanganKompetensiId: number | null
  perilakuKerjaItems: Array<PerilakuKerjaItem>
  reviewTugasTambahanIds: Array<number>
  reviewTugasPokokIds: Array<number>
  reviewPraktikKinerjaId: number | null
  reviewPengembanganKompetensiId: number | null
  reviewPerilakuKerjaItems: Array<PerilakuKerjaItem>
}

export type PerilakuKerjaItem = {
  aspekId?: number
  indikatorId?: number
  ekspektasiId?: number
  isChanged?: boolean
}
